import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/version.ts
function parseVersion(version) {
	const match = /^(\d+)\.(\d+)\.(\d+)(?:\.(\d+))?$/.exec(version);
	if (!match) return null;
	const [, major, minor, patch, rc] = match;
	return [
		Number(major),
		Number(minor),
		Number(patch),
		rc === void 0 ? Infinity : Number(rc)
	];
}
/**
* Whether the version is at least `x.y.z.(n)`.
*
* A version `x.y.z.n` includes itself and `x.y.z`
* but a `x.y.z` version doesn't include `x.y.z.n`.
*/
function isVersionAtLeast(version, minVersion) {
	const current = parseVersion(version);
	const min = parseVersion(minVersion);
	if (!current || !min) return false;
	for (let i = 0; i < current.length; i++) if (current[i] !== min[i]) return current[i] > min[i];
	return true;
}
//#endregion
//#region src/client.ts
var AppsBackendClient = class {
	constructor(url) {
		this.features = {};
		this.attributes = {};
		this.listeners = /* @__PURE__ */ new Set();
		this.snapshot = this.features;
		if (url) this.attributes.url = url;
	}
	get url() {
		return this.attributes.url ?? "";
	}
	async init() {
		await this.refreshFeatures();
	}
	async refreshFeatures() {
		try {
			const res = await fetch(`${this.url}/api/features`);
			if (!res.ok) throw new Error("Failed to fetch features");
			const data = await res.json();
			this.features = data.features;
			this.updateSnapshot();
		} catch (error) {
			console.error(error);
		}
	}
	getFeatureValue(key, defaultValue) {
		const feature = this.snapshot[key];
		if (feature && feature.defaultValue !== void 0) return feature.defaultValue;
		return defaultValue;
	}
	getFeatures() {
		return this.features;
	}
	setAttributes(attrs) {
		this.attributes = {
			...this.attributes,
			...attrs
		};
		if ("version" in attrs) this.updateSnapshot();
	}
	getAttributes() {
		return this.attributes;
	}
	async setPayload(payload) {
		this.features = payload.features;
		this.updateSnapshot();
	}
	subscribe(listener) {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}
	getSnapshot() {
		return this.snapshot;
	}
	async getCoinPrice(coin) {
		return this.request(`coin-price/${coin}`);
	}
	async getMonitorNetwork(project) {
		return this.request("monitor-network", { project });
	}
	async checkRestricted() {
		return (await fetch(`${this.url}/api/restricted/`, {
			method: "POST",
			headers: {
				accept: "",
				"content-type": "application/json"
			},
			body: JSON.stringify({})
		})).status === 403;
	}
	async request(path, queryParams, options) {
		const base = `${this.url}/${path}`;
		const url = queryParams && Object.keys(queryParams).length > 0 ? `${base}?${new URLSearchParams(queryParams)}` : base;
		const res = await fetch(url, options);
		if (!res.ok) throw new Error("Unexpected response");
		return res.json();
	}
	updateSnapshot() {
		const version = this.attributes.version;
		this.snapshot = Object.fromEntries(Object.entries(this.features).filter(([, feature]) => !feature.minVersion || typeof version !== "string" || isVersionAtLeast(version, feature.minVersion)));
		this.listeners.forEach((listener) => listener());
	}
};
//#endregion
//#region src/react.tsx
const AppsBackendClientContext = createContext(null);
function AppsBackendClientProvider({ client, children }) {
	return /* @__PURE__ */ jsx(AppsBackendClientContext.Provider, {
		value: client,
		children
	});
}
function useAppsBackendClient() {
	const client = useContext(AppsBackendClientContext);
	if (!client) throw new Error("useAppsBackendClient must be used within an AppsBackendClientProvider");
	return client;
}
function useFeature(key) {
	const client = useAppsBackendClient();
	const subscribe = useCallback((cb) => client.subscribe(cb), [client]);
	const getSnapshot = useCallback(() => client.getSnapshot(), [client]);
	const value = useSyncExternalStore(subscribe, getSnapshot)[key]?.defaultValue ?? null;
	return {
		value,
		on: !!value,
		off: !value
	};
}
function useFeatureValue(key, defaultValue) {
	const { value } = useFeature(key);
	return value ?? defaultValue;
}
function useFeatureIsOn(key) {
	const { on } = useFeature(key);
	return on;
}
//#endregion
export { AppsBackendClient, AppsBackendClientProvider, useAppsBackendClient, useFeature, useFeatureIsOn, useFeatureValue };

//# sourceMappingURL=index.es.js.map